// src/pages/finance-portal/leases/LeaseForm.js
//
// Create / edit lease (full-page).
// Backend auto-classifies (ASC 842) and computes ROU + liability schedule.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card,
    MenuItem, Select, FormControl, InputLabel, Alert, CircularProgress,
    Skeleton, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const ASSET_CLASS_OPTIONS = [
    { value: 'REAL_ESTATE', label: 'Real Estate' },
    { value: 'EQUIPMENT', label: 'Equipment' },
    { value: 'VEHICLE', label: 'Vehicle' },
    { value: 'OTHER', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'SEMI_ANNUAL', label: 'Semi-Annual' },
    { value: 'ANNUAL', label: 'Annual' },
];

const initial = {
    name: '',
    description: '',
    lessor: '',
    asset_class: 'REAL_ESTATE',
    asset_description: '',
    asset_useful_life_months: '',
    asset_fair_value: '',
    commencement_date: '',
    end_date: '',
    term_months: 60,
    payment_frequency: 'MONTHLY',
    payment_in_advance: true,
    base_rent: '',
    annual_escalation_pct: 0,
    discount_rate_pct: '',
    purchase_option_price: '',
    purchase_option_reasonably_certain: false,
    transfers_ownership: false,
    is_specialized_asset: false,
    residual_value_guarantee: 0,
    initial_direct_costs: 0,
    prepaid_rent: 0,
    incentives_received: 0,
    currency: 'USD',
    notes: '',
};

export default function LeaseForm() {
    const navigate = useNavigate();
    const { leaseId } = useParams();
    const isEdit = !!leaseId;
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState({ ...initial });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchLease = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/leases/${leaseId}`);
            if (res.data.success) {
                const l = res.data.data;
                setData({
                    name: l.name || '',
                    description: l.description || '',
                    lessor: l.lessor || '',
                    asset_class: l.asset_class || 'REAL_ESTATE',
                    asset_description: l.asset_description || '',
                    asset_useful_life_months: l.asset_useful_life_months ?? '',
                    asset_fair_value: l.asset_fair_value != null ? String(l.asset_fair_value) : '',
                    commencement_date: l.commencement_date || '',
                    end_date: l.end_date || '',
                    term_months: l.term_months ?? 60,
                    payment_frequency: l.payment_frequency || 'MONTHLY',
                    payment_in_advance: !!l.payment_in_advance,
                    base_rent: String(l.base_rent ?? ''),
                    annual_escalation_pct: l.annual_escalation_pct ?? 0,
                    discount_rate_pct: String(l.discount_rate_pct ?? ''),
                    purchase_option_price: l.purchase_option_price != null
                        ? String(l.purchase_option_price) : '',
                    purchase_option_reasonably_certain: !!l.purchase_option_reasonably_certain,
                    transfers_ownership: !!l.transfers_ownership,
                    is_specialized_asset: !!l.is_specialized_asset,
                    residual_value_guarantee: l.residual_value_guarantee ?? 0,
                    initial_direct_costs: l.initial_direct_costs ?? 0,
                    prepaid_rent: l.prepaid_rent ?? 0,
                    incentives_received: l.incentives_received ?? 0,
                    currency: l.currency || 'USD',
                    notes: l.notes || '',
                });
                if (l.status === 'TERMINATED' || l.status === 'EXPIRED') {
                    setError(`This lease is ${l.status} and cannot be edited.`);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load lease');
        } finally {
            setLoading(false);
        }
    }, [isEdit, leaseId]);

    useEffect(() => { fetchLease(); }, [fetchLease]);

    const update = (k, v) => {
        setData(prev => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const validateLocal = () => {
        const e = {};
        if (!data.name?.trim()) e.name = 'Required';
        if (!data.lessor?.trim()) e.lessor = 'Required';
        if (!data.commencement_date) e.commencement_date = 'Required';
        if (!data.end_date) e.end_date = 'Required';
        if (data.commencement_date && data.end_date && data.end_date <= data.commencement_date) {
            e.end_date = 'Must be after commencement';
        }
        const tm = parseInt(data.term_months, 10);
        if (isNaN(tm) || tm < 1) e.term_months = 'Required';
        const rent = parseFloat(data.base_rent);
        if (isNaN(rent) || rent < 0) e.base_rent = 'Must be ≥ 0';
        const dr = parseFloat(data.discount_rate_pct);
        if (isNaN(dr) || dr < 0 || dr > 100) e.discount_rate_pct = 'Must be 0..100';
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                name: data.name.trim(),
                description: data.description?.trim() || null,
                lessor: data.lessor.trim(),
                asset_class: data.asset_class,
                asset_description: data.asset_description?.trim() || null,
                asset_useful_life_months: data.asset_useful_life_months === '' ? null : parseInt(data.asset_useful_life_months, 10),
                asset_fair_value: data.asset_fair_value === '' ? null : parseFloat(data.asset_fair_value),
                commencement_date: data.commencement_date,
                end_date: data.end_date,
                term_months: parseInt(data.term_months, 10),
                payment_frequency: data.payment_frequency,
                payment_in_advance: !!data.payment_in_advance,
                base_rent: parseFloat(data.base_rent),
                annual_escalation_pct: parseFloat(data.annual_escalation_pct) || 0,
                discount_rate_pct: parseFloat(data.discount_rate_pct),
                purchase_option_price: data.purchase_option_price === ''
                    ? null : parseFloat(data.purchase_option_price),
                purchase_option_reasonably_certain: !!data.purchase_option_reasonably_certain,
                transfers_ownership: !!data.transfers_ownership,
                is_specialized_asset: !!data.is_specialized_asset,
                residual_value_guarantee: parseFloat(data.residual_value_guarantee) || 0,
                initial_direct_costs: parseFloat(data.initial_direct_costs) || 0,
                prepaid_rent: parseFloat(data.prepaid_rent) || 0,
                incentives_received: parseFloat(data.incentives_received) || 0,
                currency: (data.currency || 'USD').toUpperCase(),
                notes: data.notes?.trim() || null,
            };
            let id = leaseId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/leases/${leaseId}`, payload);
                enqueueSnackbar('Lease updated; ROU/liability recomputed if terms changed',
                                { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/leases', payload);
                id = res.data?.data?.id;
                enqueueSnackbar(res.data?.message || 'Lease created with classification + schedule',
                                { variant: 'success' });
            }
            navigate(`/finance-portal/leases/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/leases')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Lease Accounting (ASC 842)
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit ? 'Edit Lease' : 'New Lease'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Lease Identity
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Name *" size="small"
                                   value={data.name}
                                   onChange={e => update('name', e.target.value)}
                                   error={!!errors.name} helperText={errors.name || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Lessor *" size="small"
                                   value={data.lessor}
                                   onChange={e => update('lessor', e.target.value)}
                                   error={!!errors.lessor} helperText={errors.lessor || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Asset Class</InputLabel>
                            <Select value={data.asset_class} label="Asset Class"
                                    onChange={e => update('asset_class', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {ASSET_CLASS_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Currency" size="small"
                                   value={data.currency}
                                   onChange={e => update('currency', e.target.value.toUpperCase())}
                                   inputProps={{ maxLength: 3 }}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <TextField label="Asset Description" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.asset_description}
                               onChange={e => update('asset_description', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Term & Payments
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Commencement *" type="date" size="small"
                                   value={data.commencement_date}
                                   onChange={e => update('commencement_date', e.target.value)}
                                   error={!!errors.commencement_date}
                                   helperText={errors.commencement_date || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="End Date *" type="date" size="small"
                                   value={data.end_date}
                                   onChange={e => update('end_date', e.target.value)}
                                   error={!!errors.end_date}
                                   helperText={errors.end_date || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <TextField label="Term (months) *" size="small" type="number"
                                   value={data.term_months}
                                   onChange={e => update('term_months', e.target.value)}
                                   error={!!errors.term_months}
                                   helperText={errors.term_months || ' '}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Payment Frequency</InputLabel>
                            <Select value={data.payment_frequency} label="Payment Frequency"
                                    onChange={e => update('payment_frequency', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {FREQUENCY_OPTIONS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Base Rent (per period) *" size="small" type="number"
                                   value={data.base_rent}
                                   onChange={e => update('base_rent', e.target.value)}
                                   error={!!errors.base_rent}
                                   helperText={errors.base_rent || ' '}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Annual Escalation %" size="small" type="number"
                                   value={data.annual_escalation_pct}
                                   onChange={e => update('annual_escalation_pct', e.target.value)}
                                   InputProps={{
                                       endAdornment: <InputAdornment position="end"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <FormControlLabel
                        control={<Switch checked={!!data.payment_in_advance}
                                         onChange={e => update('payment_in_advance', e.target.checked)} />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>
                            Payments in advance (start of period)
                        </Typography>}
                        sx={{ mt: 1 }}
                    />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
                        Discount Rate & Direct Costs
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8', mb: 2 }}>
                        Used to compute the initial Lease Liability (PV of payments) and ROU asset.
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Discount Rate (annual %) *" size="small" type="number"
                                   value={data.discount_rate_pct}
                                   onChange={e => update('discount_rate_pct', e.target.value)}
                                   error={!!errors.discount_rate_pct}
                                   helperText={errors.discount_rate_pct || 'IBR or implicit rate'}
                                   InputProps={{
                                       endAdornment: <InputAdornment position="end"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Initial Direct Costs" size="small" type="number"
                                   value={data.initial_direct_costs}
                                   onChange={e => update('initial_direct_costs', e.target.value)}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Prepaid Rent" size="small" type="number"
                                   value={data.prepaid_rent}
                                   onChange={e => update('prepaid_rent', e.target.value)}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Lease Incentives Received" size="small" type="number"
                                   value={data.incentives_received}
                                   onChange={e => update('incentives_received', e.target.value)}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
                        ASC 842 Classification Inputs
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8', mb: 2 }}>
                        Backend will auto-classify as Operating, Finance, or Short-Term.
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Asset Useful Life (months)" size="small" type="number"
                                   value={data.asset_useful_life_months}
                                   onChange={e => update('asset_useful_life_months', e.target.value)}
                                   helperText="For 75% useful life test"
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <TextField label="Asset Fair Value" size="small" type="number"
                                   value={data.asset_fair_value}
                                   onChange={e => update('asset_fair_value', e.target.value)}
                                   helperText="For 90% PV/FV test"
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <TextField label="Purchase Option Price" size="small" type="number"
                                   value={data.purchase_option_price}
                                   onChange={e => update('purchase_option_price', e.target.value)}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Residual Value Guarantee" size="small" type="number"
                                   value={data.residual_value_guarantee}
                                   onChange={e => update('residual_value_guarantee', e.target.value)}
                                   InputProps={{
                                       startAdornment: <InputAdornment position="start"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                       sx: fieldSx,
                                   }}
                                   InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                        <FormControlLabel
                            control={<Switch checked={!!data.transfers_ownership}
                                             onChange={e => update('transfers_ownership', e.target.checked)} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>
                                Transfers ownership at end of term
                            </Typography>} />
                        <FormControlLabel
                            control={<Switch checked={!!data.purchase_option_reasonably_certain}
                                             onChange={e => update('purchase_option_reasonably_certain', e.target.checked)} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>
                                Purchase option reasonably certain to be exercised
                            </Typography>} />
                        <FormControlLabel
                            control={<Switch checked={!!data.is_specialized_asset}
                                             onChange={e => update('is_specialized_asset', e.target.checked)} />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>
                                Specialized asset (no alternative use to lessor)
                            </Typography>} />
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 1.5 }}>
                        Notes
                    </Typography>
                    <TextField size="small" fullWidth multiline rows={3}
                               value={data.notes}
                               onChange={e => update('notes', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/leases')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !!error}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                  borderRadius: '10px', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Lease'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
